import { CustomTab } from "@/components/UI/Tabs";
import { formatCurrency, formatDate, getStatusColor } from "@/utils/helper";
import { Button, Col, Modal, Row, Table, Typography } from "antd";
import { useState, useMemo } from "react";
import { EnvironmentOutlined, ClockCircleOutlined } from "@ant-design/icons";
import {
  useGetFulfillmentByReferenceQuery,
  useGetOrderByReferenceQuery,
} from "@/services/api/orders/ordersApi";
import FulfillmentProcess from "../../component/SingleOrdalFufillment";

const { Title } = Typography;

const BulkOrderDetailsModal = ({ open, onClose, orderData }) => {
  const {
    data: orderDetailsByRef,
    isLoading: orderDetailsLoading,
    isFetching,
  } = useGetOrderByReferenceQuery(orderData?.orderId, { skip: !open });

  const { data: fufillmentDetailsByRef } = useGetFulfillmentByReferenceQuery(
    orderData?.orderId,
    { skip: !open }
  );

  const orderDetails = orderDetailsByRef?.[0] || null;
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [{ label: "Order Summary" }, { label: "Fulfillment Process" }];

  // Stage mapping
  const stageMap = {
    1: "Incoming",
    2: "Confirmed",
    3: "Processing",
    4: "Available",
    5: "Pickup",
    6: "Delivery",
  };

  const currentStatusId = fufillmentDetailsByRef?.current_status_id || 1;

  // Transform API items to table format
  const orderItems = useMemo(() => {
    if (
      !orderDetails?.order_type_id ||
      !orderDetails?.items ||
      !Array.isArray(orderDetails.items)
    ) {
      return [];
    }
    // For custom event orders
    if (orderDetails.order_type_id !== 2) {
      return orderDetails.items.map((item, index) => ({
        key: item.id || index,
        id: item.id || index,
        productName: item.productable?.name || "Unknown Product",
        image:
          item.productable?.image?.url ||
          item.productable?.url ||
          "/placeholder-food.jpg",
        price: item.amount || "0.00",
        quantity: item.qty || 1,
        subtotal: (parseFloat(item.amount || 0) * (item.qty || 1)).toFixed(2),
      }));
    } else {
      // For package orders, expand package_products into individual table rows
      return orderDetails?.items.reduce((acc, item, itemIndex) => {
        const prods = (item.package_products || []).map((prod, prodIndex) => ({
          key: prod.id || `${itemIndex}-${prodIndex}`,
          id: prod.product_id || `${itemIndex}-${prodIndex}`,
          productName: prod.product?.name || "Unknown Package",
          image:
            prod.product?.image?.url ||
            prod.product?.url ||
            "/placeholder-food.jpg",
          price: prod.product?.price || "0.00",
          quantity: prod.qty || 1,
          subtotal: (
            parseFloat(prod.product?.price || 0) * (prod.qty || 1)
          ).toFixed(2),
        }));
        return acc.concat(prods);
      }, []);
    }
  }, [orderDetails]);

  // Calculate totals
  const orderTotals = useMemo(() => {
    const subtotal = orderDetails?.total_amount || 0;
    const discount = orderDetails?.discount;
    const deliveryFee = orderDetails?.delivery_fee || 0;
    const vatFee = orderDetails?.vat_fee || 0;
    const serviceFee = orderDetails?.service_fee || 0;
    const total = subtotal + discount + deliveryFee + vatFee + serviceFee;

    return {
      subtotal,
      discount,
      total,
      deliveryFee,
      vatFee,
      serviceFee,
    };
  }, [orderDetails]);

  const orderColumns = useMemo(
    () => [
      {
        title: "PRODUCT",
        dataIndex: "productName",
        key: "productName",
        render: (text, record) => (
          <div className="flex items-center gap-3">
            <img
              src={record.image || "/placeholder-food.jpg"}
              alt={text}
              className="w-12 h-12 rounded-lg object-cover"
            />
            <span className="font-medium">{text}</span>
          </div>
        ),
      },
      {
        title: "PRICE",
        dataIndex: "price",
        key: "price",
        render: (text) => `₦${parseFloat(text).toLocaleString()}.00`,
      },
      {
        title: "QUANTITY",
        dataIndex: "quantity",
        key: "quantity",
        align: "center",
        render: (text) => `x${text}`,
      },
      {
        title: "SUBTOTAL",
        dataIndex: "subtotal",
        key: "subtotal",
        align: "right",
        render: (text) => (
          <span className="font-medium">
            ₦{parseFloat(text).toLocaleString()}
          </span>
        ),
      },
    ],
    []
  );

  const renderTabContent = () => {
    if (activeTab === 0) {
      return (
        <div className="mt-6">
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={15}>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Table
                  columns={orderColumns}
                  dataSource={orderItems}
                  rowKey="id"
                  loading={orderDetailsLoading || isFetching}
                  pagination={{
                    pageSize: 4,
                    showSizeChanger: false,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }}
                />
              </div>
            </Col>
            <Col xs={24} lg={9}>
              <div className="rounded-lg border border-gray-200 mb-6">
                <div className="p-4 border-b border-gray-200 font-semibold">
                  Amount Paid by Customer
                </div>
                <div className="p-4 space-y-3">
                  {[
                    {
                      label: "Subtotal:",
                      value: formatCurrency(orderTotals.subtotal),
                    },
                    orderTotals.discount !== undefined &&
                      orderTotals.discount !== null && {
                        label: "Discount:",
                        value: formatCurrency(orderTotals.discount),
                      },
                    orderTotals.deliveryFee && {
                      label: "Delivery Fee:",
                      value: formatCurrency(orderTotals.deliveryFee),
                    },
                    orderTotals.vatFee && {
                      label: "VAT:",
                      value: formatCurrency(orderTotals.vatFee),
                    },
                    orderTotals.serviceFee && {
                      label: "Service Fee:",
                      value: formatCurrency(orderTotals.serviceFee),
                    },
                  ]
                    .filter(Boolean)
                    .map((item, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-gray-500">{item.label}</span>
                        <span className="font-medium">{item.value}</span>
                      </div>
                    ))}
                  <div className="flex justify-between pt-3 border-t border-gray-200">
                    <span className="font-semibold text-lg">Total</span>
                    <span className="font-semibold text-lg">
                      {formatCurrency(orderTotals.total)}
                    </span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row gutter={[24, 24]} className="mt-6">
            <Col xs={24} lg={15}>
              <div className="rounded-lg border border-gray-200 p-4">
                {orderDetails?.delivery_mode === "1" &&
                orderDetails?.delivery_address !== null ? (
                  <>
                    <div className="font-semibold mb-4">Delivery Location</div>
                    <div className="flex items-start gap-3">
                      <EnvironmentOutlined className="text-orange-500 text-xl mt-1" />
                      <div>
                        <div className="font-medium mb-1">
                          {orderDetails?.delivery_address ||
                            "Location not specified"}
                        </div>
                        <div className="font-medium mb-1">
                          {orderDetails?.delivery_date ||
                            "Location not specified"}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold mb-4">Pickup Location</div>
                    <div className="flex items-start gap-3">
                      <EnvironmentOutlined className="text-orange-500 text-xl mt-1" />
                      <div>
                        <div className="font-medium mb-1">
                          {orderDetails?.location?.name ||
                            "Location not specified"}
                        </div>
                        <div className="text-gray-500 text-sm">
                          {orderDetails?.location?.address ||
                            "Address details not available"}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Col>
            <Col xs={24} lg={9}>
              <div className="rounded-lg border border-gray-200 p-4 h-full">
                {orderDetails?.delivery_instruction ? (
                  <div>{orderDetails?.delivery_instruction}</div>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <div className="text-6xl mb-2 opacity-50">📝</div>
                    <div className="text-gray-400 text-sm">No instructions</div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      );
    }

    // Fulfillment Process Tab
    return (
      <FulfillmentProcess
        orderId={orderData?.originalData?.id}
        orderReference={orderData?.orderId}
        currentStatusId={currentStatusId}
        fulfillmentStages={fufillmentDetailsByRef?.fulfilment_stages || []}
        onSuccess={() => {
          // console.log("Order status updated successfully");
        }}
        onError={(error) => {
          // console.error("Failed to update order:", error);
        }}
      />
    );
  };

  const { color, backgroundColor } = getStatusColor(
    orderDetails?.status?.name || orderData?.status || "Archived"
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="space-y-1">
          <Title level={4} className="mb-2">
            Order Details{" "}
            <span
              className="px-3 py-1 rounded-lg text-sm ml-2"
              style={{ color, backgroundColor }}
            >
              {orderDetails?.status?.name || orderData?.status || "Archived"}
            </span>
          </Title>
          <p className="text-sm text-gray-400 m-0">
            See details of customer's order below
          </p>
        </div>
      }
      maskClosable
      destroyOnClose
      width="90vw"
      style={{ top: 10, maxWidth: 1400 }}
      styles={{
        body: { maxHeight: "calc(100vh - 190px)", overflowY: "auto" },
      }}
      footer={
        <Button onClick={onClose} size="large">
          Close
        </Button>
      }
    >
      <div className="min-h-[70vh] p-6">
        <Row gutter={[24, 16]} className="my-4">
          <Col xs={24} lg={5} className="space-y-6">
            <div className="pb-4 border-b border-gray-200">
              <div className="text-xs text-gray-500 mb-2">CURRENT STATUS</div>
              <div className="font-medium">{stageMap[currentStatusId]}</div>
            </div>
            <div className="py-4 border-b border-gray-200">
              <Title level={5} className="m-0 mb-2">
                #{orderDetails?.reference || "Loading..."}
              </Title>
              <div className="text-sm text-gray-400">
                Last update:{" "}
                {orderDetails?.last_order_date
                  ? formatDate(orderDetails.last_order_date)
                  : "N/A"}
              </div>
            </div>
            <div className="mt-6 space-y-6 gap-6 flex lg:block">
              {[
                {
                  label: "CUSTOMER",
                  value: orderDetails?.user?.first_name
                    ? `${orderDetails.user.first_name}${
                        orderDetails.user.last_name
                          ? " " + orderDetails.user.last_name
                          : ""
                      }`
                    : "N/A",
                  extra: [
                    orderDetails?.user?.email,
                    orderDetails?.user?.phone,
                  ].filter(Boolean),
                },
                {
                  label: "ORDER PLACED",
                  value: orderDetails?.start_date
                    ? formatDate(orderDetails.start_date)
                    : "N/A",
                },
                {
                  label: "DUE BY",
                  value: orderDetails?.estimated_end_date
                    ? formatDate(orderDetails.estimated_end_date)
                    : "N/A",
                },
                {
                  label: "COMPLETION TIME",
                  icon: <ClockCircleOutlined className="text-gray-400 mt-1" />,
                  value: orderDetails?.end_date
                    ? formatDate(orderDetails.end_date)
                    : "Pending",
                },
              ].map((item, i) => (
                <div key={i}>
                  <div className="text-xs text-gray-500 mb-2">{item.label}</div>
                  <div className={item.icon ? "flex items-start gap-2" : ""}>
                    {item.icon}
                    <div>
                      <div className="text-sm ">{item.value}</div>
                      {item.extra?.map((text, j) => (
                        <div key={j} className="text-xs text-gray-500 mt-1">
                          {text}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Col>
          <Col
            xs={24}
            lg={18}
            className="p-6 border border-gray-200 rounded-lg bg-white"
          >
            <CustomTab
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            {renderTabContent()}
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default BulkOrderDetailsModal;
